import {
  HOTBAR_SIZE,
  type HotbarSlots,
  getSpellTemplate,
} from "@vinyl/shared";
import { el } from "./dom.js";
import { bindSpellTooltip, hideItemTooltip } from "./item-tooltip.js";
import { SPELL_DRAG_MIME } from "./spellbook.js";

export interface HotbarCallbacks {
  // User wants to set slot N to spellId (drag from spellbook) or clear it
  // (right-click on a filled slot). Server validates + emits hotbarUpdate.
  onSetSlot: (slot: number, spellId: string | null) => void;
  // User pressed a hotbar key (1-8) or clicked a filled slot — server will
  // validate target + mana and enter the cast stance.
  onCast: (slot: number) => void;
}

export class HotbarBar {
  readonly root: HTMLElement;
  private slots: HotbarSlots = new Array<string | null>(HOTBAR_SIZE).fill(null);
  private activeSlot: number | null = null;

  constructor(private readonly cb: HotbarCallbacks) {
    this.root = el("div", { class: "hotbar" });
    this.render();
  }

  setSlots(hotbar: HotbarSlots): void {
    // Defensive copy with cap to HOTBAR_SIZE.
    this.slots = new Array<string | null>(HOTBAR_SIZE).fill(null);
    for (let i = 0; i < HOTBAR_SIZE && i < hotbar.length; i++) {
      this.slots[i] = hotbar[i] ?? null;
    }
    this.render();
  }

  // Highlights the slot the player is currently in spell-mode for. Pass null
  // to clear.
  setActiveSlot(slot: number | null): void {
    if (this.activeSlot === slot) return;
    this.activeSlot = slot;
    this.render();
  }

  getSpellId(slot: number): string | null {
    return this.slots[slot] ?? null;
  }

  destroy(): void {
    this.root.remove();
  }

  private render(): void {
    // Hide any tooltip anchored to a slot about to be wiped — the
    // pointerleave event doesn't fire when the element is removed
    // mid-hover.
    hideItemTooltip();
    this.root.replaceChildren();
    for (let i = 0; i < HOTBAR_SIZE; i++) {
      this.root.append(this.renderSlot(i, this.slots[i] ?? null));
    }
  }

  private renderSlot(index: number, spellId: string | null): HTMLElement {
    const tpl = spellId ? getSpellTemplate(spellId) : null;
    const isActive = this.activeSlot === index;
    const cell = el("div", {
      class: `hotbar-slot${tpl ? ` filled school-${tpl.school}` : " empty"}${isActive ? " active" : ""}`,
      ...(tpl ? {} : { title: `Slot ${index + 1} — drag a spell here` }),
    });
    cell.append(el("span", { class: "hotbar-key" }, String(index + 1)));
    if (tpl) {
      cell.append(el("span", { class: "hotbar-name" }, tpl.name));
      cell.style.cursor = "pointer";
      cell.addEventListener("click", () => this.cb.onCast(index));
      bindSpellTooltip(cell, tpl, {
        known: true,
        footerHint: "Right-click to clear",
      });
    }
    cell.addEventListener("dragover", (ev) => {
      if (ev.dataTransfer?.types.includes(SPELL_DRAG_MIME)) {
        ev.preventDefault();
        if (ev.dataTransfer) ev.dataTransfer.dropEffect = "copy";
        cell.classList.add("drag-over");
      }
    });
    cell.addEventListener("dragleave", () => cell.classList.remove("drag-over"));
    cell.addEventListener("drop", (ev) => {
      cell.classList.remove("drag-over");
      const dropped = ev.dataTransfer?.getData(SPELL_DRAG_MIME);
      if (!dropped) return;
      ev.preventDefault();
      this.cb.onSetSlot(index, dropped);
    });
    // Right-click to clear a filled slot.
    cell.addEventListener("contextmenu", (ev) => {
      if (!spellId) return;
      ev.preventDefault();
      this.cb.onSetSlot(index, null);
    });
    return cell;
  }
}

